import React, { useCallback, useMemo } from 'react'
import { TextField } from '@workwave-tidal/core/components'
import {
  IServiceProduct,
  selectAllServiceProductsForTenant,
  SERVICE_TYPE,
} from '@user/store/serviceProducts'
import { useSelector } from 'react-redux'
import { useFormikContext } from 'formik'
import { ImportUtilityModel } from '../../typings'
import { useFieldOptionsData } from '../../hooks/useFieldOptionsData'
import Autocomplete from '@mui/material/Autocomplete'

type Props = {
  name: string
  required?: boolean
}

export function ServicesOrProducts(props: Props) {
  const { name } = props
  const { setFieldValue, values, touched, errors } =
    useFormikContext<ImportUtilityModel>()

  const { allSalesTeams } = useFieldOptionsData(values)

  const serviceProducts = useSelector(selectAllServiceProductsForTenant)

  const options = useMemo(() => {
    const salesTeamId = Number(values.salesTeamId)
    if (!salesTeamId) return []

    const branchId = allSalesTeams.find(
      (team) => team.id === salesTeamId,
    )?.branchId
    if (!branchId) return []

    const serviceProductsOptions = serviceProducts.get(branchId)
    if (!serviceProductsOptions) return []

    const sortByName = (a, b) =>
      (a.name + a.name).localeCompare(b.name + b.name)

    const services = serviceProductsOptions
      .filter((item) => item.serviceType === SERVICE_TYPE.SERVICE)
      .sort(sortByName)

    const products = serviceProductsOptions
      .filter((item) => item.serviceType === SERVICE_TYPE.PRODUCT)
      .sort(sortByName)

    return [...services, ...products]
  }, [serviceProducts, values.salesTeamId, allSalesTeams])

  const onChangeService = useCallback(
    (_e, svc: IServiceProduct | null) => {
      if (!svc?.id) {
        setFieldValue(name, '')
        return
      }
      setFieldValue(name, svc)
    },
    [setFieldValue, name],
  )

  const serviceProductDisplayName = (s: IServiceProduct) => {
    if (s.description && s.name) {
      return s.description.trim() === ''
        ? s.name
        : `${s.name} - ${s.description}`
    } else if (s.name) {
      return s.name
    }
    return ''
  }

  return (
    <>
      <Autocomplete
        id="services"
        fullWidth
        size="small"
        options={options}
        value={values.service || null}
        getOptionLabel={serviceProductDisplayName}
        groupBy={(x) =>
          x.serviceType == SERVICE_TYPE.SERVICE ? 'Service' : 'Product'
        }
        onChange={onChangeService}
        loading={false}
        loadingText="Loading Services"
        renderInput={(params) => (
          <TextField
            {...params}
            name="service"
            label="Service or Product"
            variant="outlined"
            error={touched.service && !!errors.service}
            helperText={errors.service}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    </>
  )
}
